import { useEffect } from "react";
import { toast } from "react-hot-toast";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import {
  UserPerformanceFilterI,
  PerformanceTimePeriodI,
} from "shared/lib/interfaces/performance";
import {
  getStartDateOfTimePeriod,
  preparePerformanceFiltersForAPICall,
} from "shared/lib/helpers/performance";

import { useApiClient } from "@/context/api-client";
import { PerformanceDashboardQueryKeys } from "@/api/routes/performance/queries/keys";
import { TIME_INTERVALS } from "shared/lib/constants/time";

export const useFetchPerformanceDashboardMetrics = (
  filters?: UserPerformanceFilterI
) => {
  const api = useApiClient();

  const performanceDashboardApi = useQuery({
    enabled: !!filters && !!filters.selectedCampaignIds && !!filters.timePeriod,
    queryKey: PerformanceDashboardQueryKeys.dashboardMetrics(filters),
    queryFn: () => {
      const { campaignId, startDate, endDate } =
        preparePerformanceFiltersForAPICall(
          // Because of the enabled prop, we can safely assert the filters as
          // PerformanceFilterI
          filters as UserPerformanceFilterI
        );

      return api.fetchPerformanceDashboard(
        campaignId as string,
        startDate,
        endDate
      );
    },
  });

  useEffect(() => {
    if (performanceDashboardApi.isError) {
      toast.error(
        "There was an error fetching your performance data. Please, reload the page."
      );
    }
  }, [performanceDashboardApi.isError]);

  return performanceDashboardApi;
};

export const useFetchPerformanceRevenue = (
  filters?: UserPerformanceFilterI
) => {
  const api = useApiClient();

  const performanceRevenueApi = useQuery({
    enabled: !!filters && !!filters.timePeriod,
    queryKey: PerformanceDashboardQueryKeys.revenue(filters),
    queryFn: () => {
      const startDate = getStartDateOfTimePeriod(
        // Because of the enabled prop, we can safely assert the filters as
        // PerformanceFilterI
        filters?.timePeriod as PerformanceTimePeriodI
      );

      return api.fetchPerformanceRevenue(
        filters?.selectedCampaignIds || [],
        startDate
      );
    },
    select: (data) => data.data,
  });

  useEffect(() => {
    if (performanceRevenueApi.isError) {
      toast.error("Error fetching earnings. Please reload the page.");
    }
  }, [performanceRevenueApi.isError]);

  return performanceRevenueApi;
};

export type UseFetchPerformanceRevenueApiI = ReturnType<
  typeof useFetchPerformanceRevenue
>;

export const useFetchPerformanceCampaignStats = (campaignId?: string) => {
  const api = useApiClient();

  const performanceCampaignStatsApi = useQuery({
    enabled: !!campaignId,
    queryKey: PerformanceDashboardQueryKeys.campaignStats(campaignId),
    queryFn: () => api.fetchPerformanceCampaignStats(campaignId as string),
  });

  useEffect(() => {
    if (performanceCampaignStatsApi.isError) {
      toast.error(
        "Failed to fetch performance stats by campaign. Please try to reload the page."
      );
    }
  }, [performanceCampaignStatsApi.isError]);

  return performanceCampaignStatsApi;
};

export const useFetchLifetimePerformance = () => {
  const api = useApiClient();

  const lifetimePerformanceApi = useQuery({
    queryKey: PerformanceDashboardQueryKeys.lifetimePerformance(),
    queryFn: api.fetchLifetimePerformance,
  });

  useEffect(() => {
    if (lifetimePerformanceApi.isError) {
      toast.error(
        "Failed to fetch lifetime performance data. Please try to reload the page."
      );
    }
  }, [lifetimePerformanceApi.isError]);

  return lifetimePerformanceApi;
};

export const useFetchCoachingMessagesByCampaignQuery = (
  campaignId?: string
) => {
  const apiClient = useApiClient();

  const query = useInfiniteQuery({
    enabled: !!campaignId,
    queryKey: PerformanceDashboardQueryKeys.callCoachingByCampaign(campaignId),
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.coaching.getCoachingMessagesByCampaign(
        campaignId as string,
        pageParam
      );

      return response.data;
    },
    getNextPageParam: (lastPage) => lastPage?.next_token,
    initialPageParam: undefined as string | undefined,
    select: (data) => data.pages.flatMap((page) => page.coaching_messages),
  });

  useEffect(() => {
    if (query.isError) {
      toast.error("Failed to fetch coaching messages by campaign.");
    }
  }, [query.isError]);

  return query;
};

export type UseFetchCoachingMessagesByCampaignApiI = ReturnType<
  typeof useFetchCoachingMessagesByCampaignQuery
>;

export const useFetchTodayPerformance = () => {
  const api = useApiClient();

  const todayPerformanceApi = useQuery({
    queryKey: PerformanceDashboardQueryKeys.todayPerformance(),
    queryFn: api.fetchTodayPerformance,
  });

  useEffect(() => {
    if (todayPerformanceApi.isError) {
      toast.error(
        "Failed to fetch today's performance data. Please try to re-open the widget or reload the page."
      );
    }
  }, [todayPerformanceApi.isError]);

  return todayPerformanceApi;
};

export const useFetchGoalTrackerMetrics = () => {
  const api = useApiClient();

  const goalTrackerProgressApi = useQuery({
    queryKey: PerformanceDashboardQueryKeys.goalTrackerProgress(),
    queryFn: api.fetchGoalTrackerMetrics,
    staleTime: TIME_INTERVALS.ONE_MINUTE * 10,
  });

  useEffect(() => {
    if (goalTrackerProgressApi.isError) {
      toast.error(
        "Failed to fetch goal tracker metrics data. Please try to reload the page."
      );
    }
  }, [goalTrackerProgressApi.isError]);

  return goalTrackerProgressApi;
};

export const useFetchFirstCallTime = (enabled: boolean) => {
  const api = useApiClient();

  const firstCallTimeApi = useQuery({
    enabled,
    queryKey: PerformanceDashboardQueryKeys.firstTimeQuery(),
    queryFn: () => api.fetchFirstCallTime(),
  });

  useEffect(() => {
    if (firstCallTimeApi.isError) {
      toast.error(
        "Failed to fetch first call time data. Please try to reload the page."
      );
    }
  }, [firstCallTimeApi.isError]);

  return firstCallTimeApi;
};

export const useFetchPerformanceChartData = (campaignIds: string[]) => {
  const api = useApiClient();

  const performanceCampaignDataApi = useQuery({
    queryKey: PerformanceDashboardQueryKeys.performanceChartData(campaignIds),
    queryFn: () => api.fetchPerformanceChartData(campaignIds),
    select: (data) => data.data,
  });

  useEffect(() => {
    if (performanceCampaignDataApi.isError) {
      toast.error(
        "Failed to fetch performance chart data. Please try to reload the page."
      );
    }
  }, [performanceCampaignDataApi.isError]);

  return performanceCampaignDataApi;
};

export type UseFetchPerformanceChartDataApiI = ReturnType<
  typeof useFetchPerformanceChartData
>;

export const useFetchCampaignsActiveAndPast = () => {
  const api = useApiClient();

  const campaignsActiveAndPastApi = useQuery({
    queryKey: PerformanceDashboardQueryKeys.campaignsActiveAndPast(),
    queryFn: api.getCampaignsActiveAndPast,
    select: (data) => data.data,
  });

  useEffect(() => {
    if (campaignsActiveAndPastApi.isError) {
      toast.error("Error fetching all campaigns.");
    }
  }, [campaignsActiveAndPastApi.isError]);

  return campaignsActiveAndPastApi;
};
